/* 내 위치 — 지도 위의 파란 점과, 그것을 켜고 끄는 버튼.
 *
 * **거리 기준점(origin)은 건드리지 않는다.** 목록의 'N m' 와 '거리순' 은 켜든 끄든 계속
 * 회사 기준이다. 좌표가 없는 매장은 앱이 준 회사 기준 거리(distance_m)를 그대로 쓰기
 * 때문에, 기준점만 내 위치로 바꾸면 한 목록 안에 '나에게서 몇 m' 와 '회사에서 몇 m' 가
 * 섞여 정렬이 거짓말을 한다. 지도에 점을 찍는 일과 거리를 다시 재는 일은 다른 일이다.
 *
 * 오차 반경을 같이 그리는 이유: 실내나 데스크톱에서는 위치가 수백 m 씩 틀린다. 점만
 * 찍으면 그 틀린 자리를 정확한 좌표처럼 보여주게 되는데, 첫 화면이 담는 반경이 200m
 * (App.jsx 의 NEAR_M)인 앱에서 그건 '고장났다'로 읽힌다. 원이 있으면 "이 안 어딘가"로
 * 읽힌다 — 정확할 때는 원이 점 뒤에 숨으므로 조건 없이 늘 그린다.
 */

import { LocateFixed } from "lucide-react";

import { Toggle } from "@/components/ui/toggle";

import { useHeading } from "../hooks/useHeading.js";
import { cn } from "../lib/util.js";
import { Circle, Overlay } from "./Overlay.jsx";
import { FLOAT_BTN, FLOAT_ON } from "./Overlays.jsx";

/* --primary 와 같은 값이다. 카카오 도형은 색을 문자열로만 받아서 CSS 변수를 못 쓴다.
   팔레트를 고칠 일이 생기면 style.css 의 --primary 와 이 줄을 같이 봐야 한다. */
const BLUE = "#3b82f6";

/* 가게 핀(1)보다는 위, 고른 핀(10)과 말풍선(100)보다는 아래. 16px 점이 24px 핀 밑에
   깔리면 겹쳤을 때 통째로 사라진다. */
const Z = 5;

/* 보고 있는 방향을 가리키는 부채꼴. 점을 중심으로 R 만큼 뻗고 좌우 HALF 도씩 벌어진다.
   경로는 위(진북)를 향해 그려두고, 화면에서 방위만큼 시계방향으로 돌린다. */
const R = 27;
const HALF = 39;
const at = (deg) => {
  const r = ((deg - 90) * Math.PI) / 180;   // -90: SVG 는 오른쪽이 0도라 위로 돌려놓는다
  return `${(R + R * Math.cos(r)).toFixed(2)},${(R + R * Math.sin(r)).toFixed(2)}`;
};
const WEDGE = `M${R},${R} L${at(-HALF)} A${R},${R} 0 0,1 ${at(HALF)} Z`;

/* 점과 부채꼴은 한 덩어리다. useHeading 이 여기 안에 사는 이유: 나침반은 초당 몇 번씩
   울리는데, 그 상태를 App 에 두면 그때마다 목록 수백 줄까지 같이 다시 그린다. 여기서
   받으면 다시 그려지는 건 이 점 하나다. */
function Me() {
  const heading = useHeading();
  return (
    /* 크기는 점이 정한다(부채꼴은 absolute 라 상자를 안 늘린다). 그래야 이 상자의
       한가운데가 곧 점의 한가운데이고, 오버레이의 anchor 계산이 그대로 유지된다. */
    <div className="me-mk pointer-events-none relative grid place-items-center">
      {/* 나침반이 없는 기기(데스크톱 대부분)에서는 안 그린다 — useHeading 머리 주석 */}
      {heading != null && (
        <svg
          aria-hidden="true"
          className="me-cone absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-primary"
          height={R * 2}
          /* Tailwind v4 의 -translate-* 는 transform 이 아니라 translate 속성으로 나가서
             (style.css 의 reel-win 주석 참조) 여기 rotate 와 부딪히지 않는다. 둘은 따로
             쌓이고 CSS 가 translate → rotate 순서로 적용한다 = 점을 축으로 도는 부채꼴.
             transition 을 rotate 에만 걸어야 위치 갱신으로 점이 순간이동할 때 같이
             미끄러지지 않는다. */
          style={{ rotate: `${heading}deg`, transition: "rotate .2s linear" }}
          viewBox={`0 0 ${R * 2} ${R * 2}`}
          width={R * 2}
        >
          <defs>
            {/* 끝으로 갈수록 사라진다. 잘린 선으로 끝나면 방향이 아니라 '도형'으로 읽힌다.
                가운데 정거장(65%)이 꼭 필요하다. 부채꼴의 넓이는 반지름의 제곱으로 커져서
                면적 대부분이 바깥 테두리 쪽에 몰려 있는데, 0.45 → 0 으로 곧장 떨구면
                정작 넓은 부분이 거의 투명해진다. 실제로 그렇게 만들었다가 화면에서 아무것도
                안 보였다 — 렌더는 되고 있는데 눈에만 안 띄는 상태였다. */}
            <radialGradient cx="50%" cy="50%" id="me-cone-fade" r="50%">
              <stop offset="0%" stopColor="currentColor" stopOpacity=".5" />
              <stop offset="70%" stopColor="currentColor" stopOpacity=".42" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </radialGradient>
          </defs>
          <path d={WEDGE} fill="url(#me-cone-fade)" />
        </svg>
      )}
      {/* 회사 표식(OriginMarker)과 같은 지도를 나눠 쓰지만 헷갈릴 일은 없다 —
          저쪽은 96px 마스코트 그림이고 이쪽은 16px 점이다. 흰 테를 둘러야 지도
          타일이 어떤 색이든 점이 뜬다. relative 로 부채꼴 위에 올린다. */}
      <span
        className="me-dot relative size-4 rounded-full border-[3px] border-background
                   bg-primary shadow-[0_1px_4px_rgba(0,0,0,.4)]"
      />
    </div>
  );
}

export function MyLocation({ map, pos }) {
  return (
    <>
      <Circle map={map} lat={pos.lat} lng={pos.lng} radius={pos.acc} color={BLUE} />
      {/* 꼬리가 없는 표식이라 좌표가 점의 한가운데다 → yAnchor 0.5 */}
      <Overlay map={map} lat={pos.lat} lng={pos.lng} yAnchor={0.5} zIndex={Z}>
        <Me />
      </Overlay>
    </>
  );
}

/* busy = 켜긴 했는데 좌표가 아직 안 온 사이. 이 틈이 생각보다 길다 — 권한 창을 띄우고
   기다리는 동안, 실내에서 GPS 가 헤매는 동안, 길게는 시간 초과까지 15초다.
   여기를 그냥 '켜짐'으로 보여줬더니 정확히 그 질문이 돌아왔다: "켜져 있는데 아무것도
   안 보인다". 버튼이 켜졌다는 것과 위치를 찾았다는 것은 다른 말이고, 화면은 그 둘을
   구별해서 말해야 한다.

   아이콘은 안 바꾸고 숨만 쉬게 한다. 상태마다 다른 그림을 쓰면 같은 버튼이 두 개처럼
   보인다(LockButton 의 주석과 같은 이유). */
export function MyLocationButton({ on, busy, onToggle }) {
  return (
    <Toggle
      aria-label="내 위치"
      /* 지금 상태가 아니라 누르면 무슨 일이 생기는지를 적는다 — 켜고 끄는 버튼의
         설명은 그쪽이 쓸모 있다(RandomButtons.jsx 의 LockButton 과 같은 규칙).
         찾는 중일 때만 예외다. 그때는 '무슨 일이 벌어지고 있는가'가 먼저 궁금하다.
         켜져 있을 때 하는 일이 둘이라(지도 되돌리기 / 끄기) 둘 다 적는다 — 어느 쪽이냐는
         점이 화면 가운데 있느냐가 정하고, 그 판정은 App.jsx 의 atCenter 에 있다. */
      title={busy
        ? "위치를 찾는 중…"
        : on
          ? "내 위치로 지도 되돌리기 — 이미 가운데면 끕니다"
          : "내 위치 — 지금 있는 곳을 지도에 표시합니다"}
      className={cn(FLOAT_BTN, FLOAT_ON, "size-10 rounded-full text-muted-foreground")}
      id="btn-me"
      data-busy={busy || undefined}
      onPressedChange={onToggle}
      pressed={on}
    >
      <LocateFixed className={cn("size-[18px]", busy && "animate-pulse")} />
    </Toggle>
  );
}
